import React, { useEffect, useState, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../shared/components/Breadcrumbs";
import DefaultButton from "../../../shared/components/DefaultButton";
import ServerSideGrid from "../../../shared/components/listview/ServerSideGrid";
import { STORES, CREATENEW } from "../../../shared/constants/Constants";
import {
  loadStoreDataRequest,
  loadStoreImportDataRequest,
} from "../redux/Actions";
import { loadFormAllDataRequest, loadFormImportDataRequest} from "../../../containers/dynamic_form/redux/Actions"
import { loadCompanyUserGetDataRequest } from '../../company/redux/Actions'
import ImportModal from "../../../shared/components/ImportModal";
import Permissions from "../../../shared/components/Permissions";

const Stores = () => {
  let dispatch = useDispatch();
  let navigate = useNavigate();
  let { loading, stores } = useSelector((state) => state.store);
  let { userCompany } = useSelector((state) => state.company);
  let [storeData, setStoreData] = useState([]);

  // Load the user's preferred company on mount
  useEffect(() => {
    dispatch(loadCompanyUserGetDataRequest())
  }, [dispatch])

  // Once we know the user's preferred company (or lack of one), load stores accordingly
  useEffect(() => {
    const companyIds = userCompany?.data?.company_id;
    if (Array.isArray(companyIds) && companyIds.length > 0) {
      dispatch(loadStoreDataRequest(companyIds));
    } else {
      dispatch(loadStoreDataRequest());
    }
  }, [dispatch, userCompany])

  // Only show active stores on first load — grid's own filter (Active column) drives this,
  // so the user can change it afterward without us re-slicing the data.
  const defaultActiveFilter = useMemo(() => ({
    filterModel: {
      active: { filterType: 'set', values: ['Active'] },
    },
  }), []);

  // Maps the numeric "active" code to a display label, used for both rendering and the set filter
  const STATUS_LABELS = { 0: 'Active', 1: 'Inactive', 2: 'Upcoming' };

  // Status badge renderer for the "active" code (0 = Active, 1 = Inactive, 2 = Upcoming)
  const statusCellRenderer = (params) => {
    const val = params.value;
    const config = {
      0: { label: 'Active', color: '#198754', bg: 'rgba(25,135,84,0.15)' },         
      1: { label: 'Inactive', color: '#dc3545', bg: 'rgba(220,53,69,0.15)' },
      2: { label: 'Upcoming', color: '#0d6efd', bg: 'rgba(13,110,253,0.15)' },
    };
    const { label, color, bg } = config[val] || { label: '-', color: '#666', bg: 'transparent' };
    return (
      <span
        style={{
          padding: '3px 12px',
          borderRadius: '8px',
          fontWeight: 600,
          color,
          backgroundColor: bg,
          fontSize: '13px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '70px',
          height: '24px',
        }}
      >
        {label}
      </span>
    );
  };

  // Checkbox-style renderer for the "Covering" boolean column
  const coveringCellRenderer = (params) => {
    const isTrue = params.value === true || params.value === 'true' || params.value === 1 || params.value === '1';
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <i
          className={isTrue ? 'fa fa-check-square' : 'fa fa-square-o'}
          style={{ fontSize: '16px', color: isTrue ? '#198754' : '#adb5bd' }}
        ></i>
      </div>
    );
  };

  // Date columns come back as 'mm-dd-yyyy' strings — parse for correct filter comparison
  const dateFilterParams = useMemo(() => ({
    comparator: (filterLocalDateAtMidnight, cellValue) => {
      if (!cellValue) return -1;
      const [month, day, year] = cellValue.split('-').map(Number);
      const cellDate = new Date(year, month - 1, day);
      if (cellDate < filterLocalDateAtMidnight) return -1;
      if (cellDate > filterLocalDateAtMidnight) return 1;
      return 0;
    },
  }), []);

  const columnDefs = useMemo(() => [
    {
      headerName: "SAP",
      field: "sap",
      width: 90,
      pinned: 'left',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Store Name",
      field: "store_name",
      pinned: 'left',
      minWidth: 180,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Manager",
      field: "manager",
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Covering",
      field: "covering_store",
      width: 100,
      filter: false,
      sortable: false,
      cellRenderer: coveringCellRenderer,
    },
    {
      headerName: "T Mobile Name",
      field: "tmob_name",
      width: 250,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Sprint SAP",
      field: "sprint_sap",
      width: 105,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Density Class",
      field: "density_class_name",
      width: 125,
      filter: 'agSetColumnFilter',
      sortable: true,
    },
    {
      headerName: "Store Classification",
      field: "store_classification_name",
      width: 160,
      filter: 'agSetColumnFilter',
      sortable: true,
    },
    {
      headerName: "Channel Name",
      field: "channel_name",
      width: 130,
      filter: 'agSetColumnFilter',
      sortable: true,
    },
    {
      headerName: "Acquisition Dealer",
      field: "acquisition_dealer_name",
      width: 160,
      filter: 'agSetColumnFilter',
      sortable: true,
    },
    {
      headerName: "Sub Dealer Id",
      field: "sub_dealer_id",
      width: 145,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Comm Comp Group",
      field: "commission_comp_group_name",
      width: 170,
      filter: 'agSetColumnFilter',
      sortable: true,
    },
    {
      headerName: "Phone",
      field: "phone",
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Address",
      field: "address_street",
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "City",
      field: "city",
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "County",
      field: "county",
      width: 165,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "State",
      field: "state",
      width: 90,
      filter: 'agSetColumnFilter',
      sortable: true,
    },
    {
      headerName: "Zip",
      field: "zip",
      width: 115,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Acquisition Date",
      field: "acquisition_date",
      width: 150,
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
      sortable: true,
    },
    {
      headerName: "Open Date",
      field: "open_date",
      width: 115,
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
      sortable: true,
    },
    {
      headerName: "Closed Date",
      field: "closed_date",
      width: 115,
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
      sortable: true,
    },
    {
      headerName: "Temp Closed Date",
      field: "temp_closed_date",
      width: 155,
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
      sortable: true,
    },
    {
      headerName: "Relocation Date",
      field: "relocation_date",
      width: 150,
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
      sortable: true,
    },
    {
      headerName: "District",
      field: "market",
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Region",
      field: "region_name",
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Division",
      field: "division_name",
      width: 115,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Website",
      field: "website",
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Comments",
      field: "comments",
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: "Active",
      field: "active",
      width: 115,
      filter: 'agSetColumnFilter',
      filterValueGetter: (params) => STATUS_LABELS[params.data.active] ?? params.data.active,
      filterParams: { values: ['Active', 'Inactive', 'Upcoming'] },
      cellRenderer: statusCellRenderer,
      sortable: true,
    }
  ], [dateFilterParams]);

  let [showImportModal, setShowImportModal] = useState(false);
  const [importStatus, setImportStatus] = useState(false);

  let [showFormImportModal, setShowFormImportModal] = useState(false);
  const [formImportStatus, setFormImportStatus] = useState(false);
  let { allforms,  error} = useSelector((state) => state.forms);
  let [isFormupload, setIsFormupload] = useState(false);
  let [dynamicForm, setDynamicForm] = useState([]);

  useEffect(() => {
    if (importStatus && !loading) {
      setShowImportModal(false);
      setImportStatus(false);
      setIsFormupload(false);
      const companyIds = userCompany?.data?.company_id;
      if (Array.isArray(companyIds) && companyIds.length > 0) {
        dispatch(loadStoreDataRequest(companyIds));
      } else {
        dispatch(loadStoreDataRequest());
      }
    }
  }, [importStatus, loading, dispatch])

  useEffect(() => {
    console.log("loading loadFormGetDataRequest")
      dispatch(loadFormAllDataRequest());      
  }, [dispatch]);

  useEffect(() => {
    if (formImportStatus) {
      setShowFormImportModal(false);
      setFormImportStatus(false);
      dispatch(setShowFormImportModal());
    }
  }, [formImportStatus, loading, dispatch])

  // Exclude the backup/placeholder SAP store; company filtering is already done server-side
  useEffect(() => {
    setStoreData(Array.isArray(stores) ? stores.filter(store => store.sap !== 'B000') : [])
  }, [stores])

  let emp_no = JSON.parse(localStorage.getItem("emp_no"));
  let module_name = "Stores";
  let permission = Permissions(emp_no, module_name);

  return (
    <>
      <Row>
        <Col xl={4} xs={12}>
          <Breadcrumbs
            children={[
              { label: STORES },
            ]}
          />
        </Col>
        <Col xl={8} xs={12}>
          {permission.create_permission ? (
            <div className="float-end">
              <DefaultButton
                buttonClass={"default-btn-color me-2"}
                buttonTitle={"Form Import"}
                actionBlock={() => {
                  setShowFormImportModal(true);
                  setIsFormupload(true);
                  setDynamicForm(allforms);
                }}
              />
              <DefaultButton
                buttonClass={"default-btn-color me-2"}
                buttonTitle={"Import"}
                actionBlock={() => {
                  setShowImportModal(true);
                  setIsFormupload(false);
                  setDynamicForm([]);
                }}
              />              
              <DefaultButton
                buttonClass={"default-btn-color"}
                buttonTitle={CREATENEW}
                actionBlock={() => {
                  navigate("/stores/add");
                }}
              />
            </div>
          ) : (<></>)}
        </Col>
      </Row>
      <Row>
        <Card className='table-card'>
          <Card.Body >
            <ServerSideGrid
              clientSide={true}
              rowData={storeData}
              columnDefs={columnDefs}
              externalFilter={defaultActiveFilter}
              tableHeight='70vh'
              showPagination={true}
              paginationPageSize={80}
              theme="alpine"
              showExport
              showExportAll
              showClearFilter
              exportFileName={`${STORES}_${new Date().toISOString().slice(0, 10)}`}
              onRowClicked={(row) => {
                navigate("./" + row.data["id"]);
              }}
            />
          </Card.Body>
        </Card>
      </Row>

      <ImportModal
        show={showImportModal}
        handleClose={() => {
          setShowImportModal(false);
          setIsFormupload(false);
          setDynamicForm([]);
        } }
        tableName={"store"}
        modalTitle={"Import Stores"}
        importAction={loadStoreImportDataRequest}
        loadData = { null}
        isFormuplod = {isFormupload}
        dyamicFormData = {dynamicForm}                                                                                                                                                  
      />

      <ImportModal
        show={showFormImportModal}
        handleClose={() => { 
          setShowFormImportModal(false);
          setIsFormupload(false);
          setDynamicForm([]);
         } }
        tableName={"form_data"}
        modalTitle={"Import Stores Data for Store"}
        importAction={loadStoreImportDataRequest}
        loadData = { null}
        isFormuplod = {isFormupload}
        dyamicFormData = {dynamicForm}

      />

    </>
  );
};

export default Stores;